import { useEffect, useState } from "react";
import { ChevronLeft, ChevronRight, ExternalLink } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import ScrollReveal from "@/components/ScrollReveal";
import SEO from "@/components/SEO";
import { Badge } from "@/components/ui/badge";
import ruthSchoko1 from "@/assets/ruth-schoko-1.jpg";
import ruthSchoko2 from "@/assets/ruth-schoko-2.jpg";
import ruthSchoko3 from "@/assets/ruth-schoko-3.jpg";
import ruthSchoko4 from "@/assets/ruth-schoko-4.jpg";
import ruthSchoko5 from "@/assets/ruth-schoko-5.jpg";
import ruthSchoko6 from "@/assets/ruth-schoko-6.jpg";
import ruthSchoko7 from "@/assets/ruth-schoko-7.jpg";
import papageienpark1 from "@/assets/papageienpark-1.jpg";
import papageienpark2 from "@/assets/papageienpark-2.jpg";
import papageienpark3 from "@/assets/papageienpark-3.jpg";
import papageienpark4 from "@/assets/papageienpark-4.jpg";
import papageienpark5 from "@/assets/papageienpark-5.jpg";
import papageienpark6 from "@/assets/papageienpark-6.jpg";
import papageienpark7 from "@/assets/papageienpark-7.jpg";
import papageienpark8 from "@/assets/papageienpark-8.jpg";
import papageienparkFoto1 from "@/assets/papageienpark-foto-1.jpg";
import papageienparkFoto2 from "@/assets/papageienpark-foto-2.jpg";
import papageienparkFoto3 from "@/assets/papageienpark-foto-3.jpg";
import papageienparkFoto4 from "@/assets/papageienpark-foto-4.jpg";
import papageienparkFoto5 from "@/assets/papageienpark-foto-5.jpg";
import papageienparkFoto6 from "@/assets/papageienpark-foto-6.jpg";
import porscheCupPitlane from "@/assets/porsche-cup-pitlane.jpg";
import comingSoonImg from "@/assets/coming-soon.png";
import porscheCupRace from "@/assets/porsche-cup-race.jpg";
import porscheCupHelmet from "@/assets/porsche-cup-helmet.jpg";
import porscheCupDuel from "@/assets/porsche-cup-duel.jpg";
import porscheCupPortrait from "@/assets/porsche-cup-portrait.jpg";
import porscheCupBattle from "@/assets/porsche-cup-battle.jpg";
import porscheCupGrid from "@/assets/porsche-cup-grid.jpg";
import porscheCupPack from "@/assets/porsche-cup-pack.jpg";
import porscheCupGridlineup from "@/assets/porsche-cup-gridlineup.jpg";
import porscheCupPackrun from "@/assets/porsche-cup-packrun.jpg";
import grasbahnrennen1 from "@/assets/grasbahnrennen-1.jpg";
import grasbahnrennen2 from "@/assets/grasbahnrennen-2.jpg";
import grasbahnrennen3 from "@/assets/grasbahnrennen-3.jpg";
import grasbahnrennen4 from "@/assets/grasbahnrennen-4.jpg";
import grasbahnrennen5 from "@/assets/grasbahnrennen-5.jpg";
import grasbahnrennen6 from "@/assets/grasbahnrennen-6.jpg";
import grasbahnrennen7 from "@/assets/grasbahnrennen-7.jpg";
import limitlessLhHero from "@/assets/limitless-lh-hero.png";
import huggiberriesHero from "@/assets/huggiberries-hero.png";
import tusHarpenLogo from "@/assets/tus-harpen-logo.jpg";
import tusHarpen1 from "@/assets/tus-harpen-1.jpg";
import tusHarpen2 from "@/assets/tus-harpen-2.jpg";
import tusHarpen3 from "@/assets/tus-harpen-3.jpg";
import tusHarpen4 from "@/assets/tus-harpen-4.jpg";
import tusHarpen5 from "@/assets/tus-harpen-5.jpg";
import tusHarpen6 from "@/assets/tus-harpen-6.jpg";
import tusHarpen7 from "@/assets/tus-harpen-7.jpg";
import tusHarpen8 from "@/assets/tus-harpen-8.jpg";
import tusHarpen9 from "@/assets/tus-harpen-9.jpg";
import tusHarpen10 from "@/assets/tus-harpen-10.jpg";
import tusHarpen11 from "@/assets/tus-harpen-11.jpg";

const categories = ["Alle", "Video", "Foto", "Web", "Social Media"];

interface Project {
  title: string;
  category: string | string[];
  description: string;
  gradient: string;
  videoId?: string;
  videoSrc?: string;
  image?: string;
  images?: string[];
  archived?: boolean;
  url?: string;
}

const projects: Project[] = [
  { title: "PORSCHE CARRERA CUP ITALIA IMOLA", category: "Video", description: "Wir begleiten den Lirim Zendeli, ehemaligen Formel-4-Champion, während eines Rennwochenendes im Porsche Carrera Cup Italia auf dem Autodromo Enzo e Dino Ferrari.\n\nIm Fokus steht die Produktion eines cineastischen After Movies, das die Intensität, Emotionen und entscheidenden Momente des Rennwochenendes authentisch einfängt von den Vorbereitungen im Fahrerlager bis zum Geschehen auf der Strecke.", gradient: "from-primary/30 to-deep-blue/40", videoSrc: "/videos/porsche-carrera-cup-italia-2025.mp4" },
   
   { title: "Ruth Schokoladenwerk GmbH", category: "Foto", description: "Für den Relaunch der Webseite haben wir über 150 hochwertige Produktfotos produziert, die die Qualität, Details und Ästhetik der Produkte optimal in Szene setzen und für einen konsistenten Markenauftritt sorgen.", gradient: "from-deep-purple/40 to-primary/30", images: [ruthSchoko1, ruthSchoko2, ruthSchoko3, ruthSchoko4, ruthSchoko5, ruthSchoko6, ruthSchoko7] },
   { title: "Papageienpark Bochum Heike Mundt GmbH", category: "Social Media", description: "Seit 2024 verantworten wir die Content-Strategie und Umsetzung auf TikTok, Instagram und Facebook. Über einen Zeitraum von 90 Tagen konnten wir durch eine gezielte Kampagne plattformübergreifend 6,7 Millionen Views sowie mehr als 26.000 neue Follower generieren.\n\nDas Ergebnis: eine schnelle, organische Skalierung der Reichweite und ein deutlich messbares Wachstum der Community.", gradient: "from-primary/20 to-deep-purple/40", image: papageienpark1 },
   { title: "\u200bGrasbahnrennen Lüdinghausen  Team Limitless", category: "Video", description: "Wir haben Team Limitless beim Grasbahnrennen Lüdinghausen 2025 am Renntag begleitet und ein dynamisches Aftermovie produziert, das die Atmosphäre und Highlights des Events authentisch einfängt.", gradient: "from-deep-blue/30 to-deep-purple/30", videoSrc: "/videos/grasbahnrennen-luedinghausen-2025.mp4" },
   
   { title: "Limitless LH", category: "Web", description: "Konzeption & Umsetzung der Webseite für Team Limitless – ein moderner, performanter Webauftritt, der die Energie und den professionellen Anspruch des Teams digital erlebbar macht.", gradient: "from-deep-blue/40 to-primary/30", url: "https://limitless-lh.de", image: limitlessLhHero },
  {
    title: "PORSCHE CARRERA CUP ITALIA IMOLA",
    category: "Foto",
    description: "Wir begleiten den Lirim Zendeli, ehemaligen Formel-4-Champion, während eines Rennwochenendes im Porsche Carrera Cup Italia auf dem Autodromo Enzo e Dino Ferrari.\n\nDabei entstehen sowohl ein cineastisches After Movie als auch eine hochwertige Fotodokumentation, die die Intensität, Emotionen und entscheidenden Momente des Rennwochenendes authentisch festhalten  von den Vorbereitungen im Fahrerlager bis zur Action auf der Strecke.",
    gradient: "from-deep-purple/30 to-primary/30",
    images: [
      porscheCupPitlane,
      porscheCupRace,
      porscheCupHelmet,
      porscheCupDuel,
      porscheCupPortrait,
      porscheCupBattle,
      porscheCupGrid,
      porscheCupPack,
      porscheCupGridlineup,
      porscheCupPackrun,
    ],
  },
   { title: "VfL SparkassenStars Bochum", category: ["Social Media", "Video"], description: "Für das 3x3-Event haben wir die Content-Produktion umgesetzt und dynamische Social-Media-Inhalte erstellt, die die Energie des Events einfangen und die digitale Präsenz der Marke stärken.", gradient: "from-deep-blue/30 to-primary/20", videoSrc: "/videos/sparkassenstars-4facemedia.mp4" },
   { title: "Grasbahnrennen Lüdinghausen", category: "Foto", description: "Fotografische Dokumentation des Grasbahnrennens Lüdinghausen dynamische Aufnahmen, die die Geschwindigkeit und Atmosphäre des Events festhalten.", gradient: "from-primary/30 to-deep-blue/30", images: [grasbahnrennen1, grasbahnrennen2, grasbahnrennen3, grasbahnrennen4, grasbahnrennen5, grasbahnrennen6, grasbahnrennen7] },
   { title: "Papageienpark Bochum", category: "Foto", description: "Hochwertige Fotografie für den Papageienpark Bochum – farbenfrohe Aufnahmen, die die Vielfalt und Faszination der exotischen Vögel einfangen.", gradient: "from-deep-purple/30 to-primary/30", images: [papageienparkFoto1, papageienparkFoto2, papageienparkFoto3, papageienparkFoto4, papageienparkFoto5, papageienparkFoto6] },
   { title: "TuS Harpen vs. VfL Bochum 1848", category: "Foto", description: "Fotografische Begleitung des Testspiels zwischen TuS Harpen und VfL Bochum 1848 – emotionale Momente, packende Spielszenen und authentische Eindrücke vom Spieltag.", gradient: "from-primary/30 to-deep-blue/40", images: [tusHarpen1, tusHarpen2, tusHarpen3, tusHarpen4, tusHarpen5, tusHarpen6, tusHarpen7, tusHarpen8, tusHarpen9, tusHarpen10, tusHarpen11] },
];

const Projekte = () => {
  const [filter, setFilter] = useState("Alle");
  const [sharedSlideIndex, setSharedSlideIndex] = useState(0);

  const filtered = (filter === "Alle" ? projects : projects.filter((p) => Array.isArray(p.category) ? p.category.includes(filter) : p.category === filter)).filter((p) => !p.archived);

  // Auto-advance all slideshows in sync
  const hasSlideshows = projects.some((p) => p.images && p.images.length > 1);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (!hasSlideshows) return;
    const interval = setInterval(() => {
      setSharedSlideIndex((prev) => prev + 1);
    }, 5000);
    return () => clearInterval(interval);
  }, [hasSlideshows]);

  const handleSlidePrev = () => {
    setSharedSlideIndex((prev) => prev - 1);
  };

  const handleSlideNext = () => {
    setSharedSlideIndex((prev) => prev + 1);
  };

  return (
    <div className="min-h-screen bg-background">
      <SEO title="Projekte & Portfolio – Videoproduktion, Fotografie & Webdesign | 4Facemedia Bochum" description="Entdecke unser Portfolio: Videoproduktion, Fotografie, Webdesign & Social Media Projekte. Kreativagentur aus Bochum, deutschlandweit im Einsatz." path="/projekte" />
      <Header />

      <section className="pt-32 pb-20 bg-gradient-hero">
        <div className="container mx-auto px-4">
          <ScrollReveal>
            <span className="text-primary text-sm font-medium tracking-wider uppercase">Portfolio</span>
            <h1 className="text-5xl md:text-7xl font-bold text-foreground mt-4 mb-6">
              Unsere <span className="text-gradient">Projekte</span>
            </h1>
            <p className="text-xl text-foreground/85 max-w-2xl">
              Ausgewählte Arbeiten aus den Bereichen Videoproduktion, Fotografie, Webdesign & Social Media. Von Bochum aus deutschlandweit realisiert.
            </p>
          </ScrollReveal>
        </div>
      </section>

      {/* Filter */}
      <section className="py-8 border-b border-border fixed top-20 left-0 right-0 z-30 glass">

        <div className="container mx-auto px-4">
          <div className="flex flex-wrap gap-2">
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => setFilter(cat)}
                className={`px-5 py-2 rounded-full text-sm font-medium transition-all ${
                  filter === cat
                    ? "bg-primary text-primary-foreground"
                    : "bg-muted/50 text-muted-foreground hover:text-foreground hover:bg-muted"
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 pt-32">

        {(() => { const isLarge = filter === "Video" || filter === "Foto" || filter === "Social Media" || filter === "Web"; return (
        <div className={isLarge ? "w-full px-0" : "container mx-auto px-4"}>
          <div className={isLarge ? "w-full space-y-16" : "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"}>


            {filtered.map((project, i) => (
              <ScrollReveal key={`${project.title}-${i}`} delay={i * 80}>
                {isLarge ? (
                  <div className="group rounded-2xl overflow-hidden border border-border/50 bg-card hover:border-primary/30 transition-all duration-500">
                    <div className="aspect-video w-full bg-black relative overflow-hidden">
                      {project.videoSrc ? (
                        <video
                          src={project.videoSrc}
                          controls
                          autoPlay
                          muted
                          loop
                          playsInline
                          preload="auto"
                          className="w-full h-full object-cover"
                        />
                      ) : project.videoId ? (
                        <iframe
                          src={`https://www.youtube-nocookie.com/embed/${project.videoId}?rel=0&modestbranding=1`}
                          title={project.title}
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                          className="w-full h-full"
                          style={{ border: 0 }}
                        />
                      ) : project.images?.length ? (
                        <>
                          <img
                            src={project.images[((sharedSlideIndex % project.images.length) + project.images.length) % project.images.length]}
                            alt={`${project.title} ${((((sharedSlideIndex % project.images.length) + project.images.length) % project.images.length) + 1)}`}
                            className="absolute inset-0 w-full h-full object-cover"
                          />
                          {project.images.length > 1 && (
                            <>
                              <button
                                type="button"
                                onClick={() => handleSlidePrev()}
                                aria-label="Vorheriges Bild"
                                className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-background/70 border border-border text-foreground hover:bg-background transition-colors flex items-center justify-center"
                              >
                                <ChevronLeft size={20} />
                              </button>
                              <button
                                type="button"
                                onClick={() => handleSlideNext()}
                                aria-label="Nächstes Bild"
                                className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-background/70 border border-border text-foreground hover:bg-background transition-colors flex items-center justify-center"
                              >
                                <ChevronRight size={20} />
                              </button>
                              <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
                                {project.images.map((_, slideIndex) => (
                                  <span
                                    key={`${project.title}-${slideIndex}`}
                                    className={`h-1.5 rounded-full transition-all ${
                                      slideIndex === (((sharedSlideIndex % project.images!.length) + project.images!.length) % project.images!.length) ? "w-6 bg-primary" : "w-2 bg-foreground/40"
                                    }`}
                                  />
                                ))}
                              </div>
                            </>
                          )}
                        </>
                      ) : project.image ? (
                        <img
                          src={project.image}
                          alt={project.title}
                          className="absolute inset-0 w-full h-full object-cover"
                        />
                      ) : (
                        <div className={`absolute inset-0 bg-gradient-to-br ${project.gradient}`} />
                      )}
                    </div>
                    <div className="p-6 md:p-8">
                      {(Array.isArray(project.category) ? project.category : [project.category]).map((cat) => (
                        <Badge key={cat} variant="secondary" className="mb-3 mr-1 bg-primary/10 text-primary border-0 text-xs">
                          {cat}
                        </Badge>
                      ))}
                      <h3 className="text-xl md:text-2xl font-semibold text-foreground mb-3">{project.title}</h3>
                      <p className="text-sm text-foreground/85 leading-relaxed max-w-3xl whitespace-pre-line">
                        {project.description}
                      </p>
                      {project.url && (
                        <div className="mt-4 pt-4 border-t border-border/50">
                          <a
                            href={project.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-2 text-sm font-medium text-primary hover:text-primary/80 transition-colors group/link"
                          >
                            <span className="truncate">{project.url.replace(/^https?:\/\//, "")}</span>
                            <ExternalLink size={14} className="transition-transform group-hover/link:translate-x-0.5 group-hover/link:-translate-y-0.5" />
                          </a>
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                <div className="group rounded-2xl overflow-hidden border border-border/50 hover:border-primary/30 transition-all duration-500 hover:-translate-y-1">
                  {/* Visual */}
                  <div className={`h-56 relative overflow-hidden ${!project.videoId && !project.videoSrc && !project.image && !project.images?.length ? `bg-gradient-to-br ${project.gradient}` : ''}`}>
                    {project.videoSrc ? (
                      <video
                        src={project.videoSrc}
                        controls
                        autoPlay
                        muted
                        loop
                        playsInline
                        preload="auto"
                        className="absolute inset-0 w-full h-full object-cover"
                      />
                    ) : project.videoId ? (
                      <iframe
                        src={`https://www.youtube-nocookie.com/embed/${project.videoId}?rel=0`}
                        title={project.title}
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                        className="absolute inset-0 w-full h-full"
                      />
                    ) : project.images?.length ? (
                      <>
                        <img
                          src={project.images[((sharedSlideIndex % project.images.length) + project.images.length) % project.images.length]}
                          alt={`${project.title} ${((((sharedSlideIndex % project.images.length) + project.images.length) % project.images.length) + 1)}`}
                          className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                        />
                        {project.images.length > 1 && (
                          <>
                            <button
                              type="button"
                              onClick={() => handleSlidePrev()}
                              aria-label="Vorheriges Bild"
                              className="absolute left-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-background/70 border border-border text-foreground hover:bg-background transition-colors flex items-center justify-center"
                            >
                              <ChevronLeft size={18} />
                            </button>
                            <button
                              type="button"
                              onClick={() => handleSlideNext()}
                              aria-label="Nächstes Bild"
                              className="absolute right-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-background/70 border border-border text-foreground hover:bg-background transition-colors flex items-center justify-center"
                            >
                              <ChevronRight size={18} />
                            </button>
                            <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
                              {project.images.map((_, slideIndex) => (
                                <span
                                  key={`${project.title}-${slideIndex}`}
                                  className={`h-1.5 rounded-full transition-all ${
                                    slideIndex === (((sharedSlideIndex % project.images!.length) + project.images!.length) % project.images!.length) ? "w-6 bg-primary" : "w-2 bg-foreground/40"
                                  }`}
                                />
                              ))}
                            </div>
                          </>
                        )}
                      </>
                    ) : project.image ? (
                      <img
                        src={project.image}
                        alt={project.title}
                        className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    ) : (
                      <div className={`absolute inset-0 bg-gradient-to-br ${project.gradient}`}>
                        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                          <div className="w-14 h-14 rounded-full glass flex items-center justify-center">
                            <ExternalLink size={22} className="text-foreground" />
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                  {/* Info */}
                  <div className="p-6 bg-card">
                    {(Array.isArray(project.category) ? project.category : [project.category]).map((cat) => (
                      <Badge key={cat} variant="secondary" className="mb-3 mr-1 bg-primary/10 text-primary border-0 text-xs">
                        {cat}
                      </Badge>
                    ))}
                    <h3 className="text-lg font-semibold text-foreground mb-2">{project.title}</h3>
                    <p className="text-sm text-foreground/85 leading-relaxed">
                      {project.description.split(/(90 Tagen|26\.000 neue Follower|6,7 Millionen Views|Lirim Zendeli|Formel-4-Champion|Porsche Carrera Cup Italia|After Movies|Team Limitless|Grasbahnrennen Lüdinghausen|Grasbahnrennens Lüdinghausen|Fotografische Dokumentation|Aftermovie|150|Produktfotos|Webseite|Markenauftritt|3x3-Event|Social-Media-Inhalte|Events)/).map((part, idx) =>
                        ["90 Tagen", "26.000 neue Follower", "6,7 Millionen Views", "Lirim Zendeli", "Formel-4-Champion", "Porsche Carrera Cup Italia", "After Movies", "Team Limitless", "Grasbahnrennen Lüdinghausen", "Grasbahnrennens Lüdinghausen", "Fotografische Dokumentation", "Aftermovie", "150", "Produktfotos", "Webseite", "Markenauftritt", "3x3-Event", "Social-Media-Inhalte", "Events"].includes(part)
                          ? <span key={idx} className="text-foreground font-bold">{part}</span>
                          : part
                      )}
                    </p>
                    {project.url && (
                      <div className="mt-4 pt-4 border-t border-border/50">
                        <a
                          href={project.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 text-sm font-medium text-primary hover:text-primary/80 transition-colors group/link"
                        >
                          <span className="truncate">{project.url.replace(/^https?:\/\//, "")}</span>
                          <ExternalLink size={14} className="transition-transform group-hover/link:translate-x-0.5 group-hover/link:-translate-y-0.5" />
                        </a>
                      </div>
                    )}
                  </div>
                </div>
                )}
              </ScrollReveal>
            ))}
          </div>
        </div>
        ); })()}
      </section>


      <Footer />
    </div>
  );
};

export default Projekte;
